import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { useClient } from '../connect_api';
import { ActuatorInfo, ActuatorType, MachineService } from '../api/api_pb';

interface EditActuatorModalProps {
    isOpen: boolean;
    onClose: () => void;
    machineUuid: string;
    onActuatorUpdated: () => void;
    actuator: ActuatorInfo | null;
    isEdit: boolean;
}

const EditActuatorModal: React.FC<EditActuatorModalProps> = ({ isOpen, onClose, machineUuid, onActuatorUpdated, actuator, isEdit }) => {
    const machineClient = useClient(MachineService);
    const [identity, setIdentity] = useState(actuator?.identity || '');
    const [ipAddress, setIpAddress] = useState(actuator?.ipAddress || '');
    const [port, setPort] = useState<number | undefined>(actuator?.port);
    const [sequence, setSequence] = useState<number | undefined>(actuator?.sequence);

    const handleSubmit = async () => {
        if (!identity.trim()) {
            toast.error('Identity is required!');
            return;
        }

        if (!ipAddress.trim()) {
            toast.error('IP Address is required!');
            return;
        }

        if (port === 0) {
            toast.error('Port is required!');
            return;
        }

        try {
            if (isEdit) {
                await machineClient.updateMachineActuator({
                    uuid: actuator?.uuid || '',
                    identity: identity,
                    ipAddress: ipAddress,
                    port: port,
                    sequence: sequence,
                    // type only locker is supported for now
                    type: Number(ActuatorType.LOCKER),
                });
                toast.success('Stock updated successfully!');
            } else {
                await machineClient.createMachineActuator({
                    machineUuid: machineUuid,
                    identity: identity,
                    ipAddress: ipAddress,
                    port: port,
                    sequence: sequence,
                     // type only locker is supported for now
                    type: Number(ActuatorType.LOCKER),
                });
                toast.success('Stock created successfully!');
            }

            onClose();
            onActuatorUpdated();
        } catch (error) {
            toast.error('Failed to trackable item: ' + (error as Error).message);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50 z-50">
            <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-lg">
                <h2 className="text-xl font-bold mb-4">{isEdit ? 'Edit' : 'Create'} Actuator</h2>

                <label className="block text-sm font-medium">Identity</label>
                <input
                    type="text"
                    value={identity}
                    onChange={(e) => setIdentity(e.target.value)}
                    className="border p-2 w-full mb-2"
                    placeholder="Enter actuator identity"
                />

                <label className="block text-sm font-medium">Sequence</label>
                <input
                    type="text"
                    value={sequence}
                    onChange={(e) => {
                        if (e.target.value === '') {
                            setSequence(undefined)
                            return;
                        }
                        if (isNaN(Number(e.target.value))) {
                            toast.error('Sequence must be a number!');
                            return;
                        }
                        setSequence(Number(e.target.value))
                    }}
                    className="border p-2 w-full mb-2"
                    placeholder="Enter actuator sequence"
                />


                <label className="block text-sm font-medium">IP Address</label>
                <input
                    type="text"
                    value={ipAddress}
                    onChange={(e) => setIpAddress(e.target.value)}
                    className="border p-2 w-full mb-2"
                    placeholder="Enter actuator IP address"
                />

                <label className="block text-sm font-medium">Port</label>
                <input
                    type="text"
                    value={port}
                    onChange={(e) => {
                        if (e.target.value === '') {
                            setPort(undefined)
                            return;
                        }
                        if (isNaN(Number(e.target.value))) {
                            toast.error('Port must be a number!');
                            return;
                        }
                        setPort(Number(e.target.value))
                    }}
                    className="border p-2 w-full mb-2"
                    placeholder="Enter actuator port"
                />


                <div className="mt-4 flex justify-end">
                    <button onClick={onClose} className="bg-gray-300 px-4 py-2 rounded-md">Cancel</button>
                    <button onClick={handleSubmit} className="bg-blue-600 text-white px-4 py-2 rounded-md ml-2">
                        {isEdit ? 'Update' : 'Create'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EditActuatorModal;
